import { cn } from '../../lib/utils'

/**
 * Predicate for "quote approved and ready to start work". Fail-closed:
 * needsReapproval must be explicitly false — an undefined field (stale
 * payload, renamed key) must never read as "ready to start", since the
 * label invites the mechanic to begin work at the approved amount.
 */
export function isJobReadyToStart(job: {
  stage?: string
  computed?: { approvalStatus?: string; needsReapproval?: boolean } | null
}): boolean {
  return (
    job?.stage === 'quote' &&
    job?.computed?.approvalStatus === 'approved' &&
    job?.computed?.needsReapproval === false
  )
}

/**
 * Calm text label for approved-ready jobs in list views. Replaces the stage
 * pill (there is only ever one status element per row) — no pill background,
 * no all-caps, no bright green.
 *
 * Unified muted-green family (single source of truth for the approved-ready
 * signal): base #57A773, darkened #2E7D4F for text on light surfaces, light
 * #8FD0A4 for text on dark surfaces. Tints are the base green at low
 * opacity so bar, tint, and label never visibly clash.
 */
export const READY_BAR_CLASS = 'bg-[#57A773]'
export const READY_TINT_CLASS = 'bg-[#57A773]/[0.06] hover:bg-[#57A773]/[0.10]'
/** Table rows: accent bar via inset box-shadow so column geometry never shifts. */
export const READY_ROW_CLASS = `${READY_TINT_CLASS} shadow-[inset_3px_0_0_#57A773]`
export const READY_LABEL_CLASS = 'text-[#2E7D4F] dark:text-[#8FD0A4]'

export function ReadyToStartLabel({ className }: { className?: string }) {
  return (
    <span className={cn('shrink-0 text-xs font-semibold', READY_LABEL_CLASS, className)}>
      <span className="sr-only">Approved — </span>
      Ready to start
    </span>
  )
}
